/**
 * Game Engine - Pure Business Logic
 * Handles all game mechanics without UI dependencies
 */

import type { InteractiveGameState, SkillCategory } from '../types'
import { getSkillById } from '../data/skills-config'
import { getActiveSynergies } from '../data/synergies-config'

export class GameEngine {
  private state: InteractiveGameState

  constructor(initialState: InteractiveGameState) {
    this.state = initialState
  }

  /**
   * Get current state (immutable)
   */
  getState(): InteractiveGameState {
    return { ...this.state }
  }

  /**
   * Game loop tick - updates passive generation
   * @param deltaTime Time elapsed since last tick in seconds
   * @returns Updated game state
   */
  tick(deltaTime: number): InteractiveGameState {
    const now = Date.now()
    this.state.lastTick = now

    // Calculate passive income
    const income = this.calculatePassiveIncome()

    // Apply income for the time elapsed (capped to max)
    this.state.resources.infrastructure = Math.min(
      this.state.resources.infrastructure + income.infrastructure * deltaTime,
      this.state.resourceCaps.infrastructure
    )
    this.state.resources.automation = Math.min(
      this.state.resources.automation + income.automation * deltaTime,
      this.state.resourceCaps.automation
    )
    this.state.resources.data = Math.min(
      this.state.resources.data + income.data * deltaTime,
      this.state.resourceCaps.data
    )
    this.state.resources.operations = Math.min(
      this.state.resources.operations + income.operations * deltaTime,
      this.state.resourceCaps.operations
    )

    // Update playtime
    this.state.playtime += deltaTime

    return this.getState()
  }

  /**
   * Manual click on a resource
   * @param resource Resource category to generate
   * @returns Updated game state
   */
  click(resource: SkillCategory): InteractiveGameState {
    // Base click value: 12 (affected by multipliers) - balanced for 8-12 min gameplay
    const baseValue = 12
    const multipliedValue =
      baseValue * this.state.multipliers.global * this.state.multipliers.activePlay

    // Map skill categories to resource types
    const resourceMap: Record<SkillCategory, keyof InteractiveGameState['resources']> = {
      cloud: 'infrastructure',
      cicd: 'automation',
      data: 'data',
      ops: 'operations',
      soft: 'operations', // Soft skills contribute to operations
    }

    const resourceKey = resourceMap[resource]
    if (resourceKey) {
      this.state.resources[resourceKey] = Math.min(
        this.state.resources[resourceKey] + multipliedValue,
        this.state.resourceCaps[resourceKey]
      )
      this.state.totalClicks++
    }

    return this.getState()
  }

  /**
   * Attempt to unlock a skill
   * @param skillId Skill ID to unlock
   * @returns Success status and message
   */
  unlockSkill(skillId: string): { success: boolean; message: string } {
    const skill = getSkillById(skillId)
    if (!skill) {
      return { success: false, message: 'Skill not found' }
    }

    // Check if already unlocked
    if (this.state.unlockedSkills.has(skillId)) {
      return { success: false, message: 'Already unlocked' }
    }

    // Check prerequisites
    if (skill.requires && skill.requires.length > 0) {
      const missingPrereqs = skill.requires.filter((req) => !this.state.unlockedSkills.has(req))
      if (missingPrereqs.length > 0) {
        const prereqNames = missingPrereqs.map((id) => getSkillById(id)?.name || id).join(', ')
        return { success: false, message: `Requires: ${prereqNames}` }
      }
    }

    // Check cost
    const resourceMap: Record<SkillCategory, keyof InteractiveGameState['resources']> = {
      cloud: 'infrastructure',
      cicd: 'automation',
      data: 'data',
      ops: 'operations',
      soft: 'operations',
    }

    const resourceKey = resourceMap[skill.cost.resource]
    const currentAmount = this.state.resources[resourceKey]

    if (currentAmount < skill.cost.amount) {
      return {
        success: false,
        message: `Need ${skill.cost.amount} ${skill.cost.resource} (have ${Math.floor(currentAmount)})`,
      }
    }

    // Deduct cost
    this.state.resources[resourceKey] -= skill.cost.amount

    // Unlock skill
    this.state.unlockedSkills.add(skillId)
    this.state.skillLevels[skillId] = 1

    // Apply multiplier if skill has one
    if (skill.multiplier) {
      this.state.multipliers[skill.multiplier.type] *= skill.multiplier.value
    }

    // Update resource caps
    this.updateResourceCaps()

    // Update synergies
    this.updateSynergies()

    return { success: true, message: `${skill.name} unlocked!` }
  }

  /**
   * Calculate passive income per second
   * Returns generation rates for each resource type
   */
  calculatePassiveIncome(): InteractiveGameState['resources'] {
    const income = {
      infrastructure: 0,
      automation: 0,
      data: 0,
      operations: 0,
    }

    // Sum up generation from all unlocked skills
    for (const skillId of this.state.unlockedSkills) {
      const skill = getSkillById(skillId)
      if (skill?.generation) {
        const resourceMap: Record<SkillCategory, keyof InteractiveGameState['resources']> = {
          cloud: 'infrastructure',
          cicd: 'automation',
          data: 'data',
          ops: 'operations',
          soft: 'operations',
        }

        const resourceKey = resourceMap[skill.generation.resource]
        if (resourceKey) {
          income[resourceKey] += skill.generation.rate
        }
      }
    }

    // Apply global multiplier
    income.infrastructure *= this.state.multipliers.global
    income.automation *= this.state.multipliers.global
    income.data *= this.state.multipliers.global
    income.operations *= this.state.multipliers.global

    // Apply cross-category multiplier
    income.infrastructure *= this.state.multipliers.crossCategory
    income.automation *= this.state.multipliers.crossCategory
    income.data *= this.state.multipliers.crossCategory
    income.operations *= this.state.multipliers.crossCategory

    // Apply synergy bonuses
    const activeSynergies = getActiveSynergies(this.state.unlockedSkills)
    for (const synergy of activeSynergies) {
      const resourceMap: Record<SkillCategory, keyof InteractiveGameState['resources']> = {
        cloud: 'infrastructure',
        cicd: 'automation',
        data: 'data',
        ops: 'operations',
        soft: 'operations',
      }

      const resourceKey = resourceMap[synergy.bonus.resource]
      if (resourceKey) {
        income[resourceKey] *= 1 + synergy.bonus.multiplier
      }
    }

    return income
  }

  /**
   * Update active synergies based on unlocked skills
   */
  updateSynergies(): void {
    const activeSynergies = getActiveSynergies(this.state.unlockedSkills)
    this.state.activeSynergies = new Set(activeSynergies.map((s) => s.id))
  }

  /**
   * Update resource caps based on unlocked skills
   * Base cap: 100, +10000 per skill in that category
   */
  updateResourceCaps(): void {
    const baseCap = 100
    const capPerSkill = 10000

    const caps = {
      infrastructure: baseCap,
      automation: baseCap,
      data: baseCap,
      operations: baseCap,
    }

    // Count skills per category
    for (const skillId of this.state.unlockedSkills) {
      const skill = getSkillById(skillId)
      if (!skill) continue

      const resourceMap: Record<SkillCategory, keyof InteractiveGameState['resources']> = {
        cloud: 'infrastructure',
        cicd: 'automation',
        data: 'data',
        ops: 'operations',
        soft: 'operations', // Soft skills contribute to ops cap
      }

      const resourceKey = resourceMap[skill.category]
      if (resourceKey) {
        caps[resourceKey] += capPerSkill
      }
    }

    this.state.resourceCaps = caps
  }

  /**
   * Check if a skill can be afforded
   */
  canAfford(skillId: string): boolean {
    const skill = getSkillById(skillId)
    if (!skill) return false

    const resourceMap: Record<SkillCategory, keyof InteractiveGameState['resources']> = {
      cloud: 'infrastructure',
      cicd: 'automation',
      data: 'data',
      ops: 'operations',
      soft: 'operations',
    }

    const resourceKey = resourceMap[skill.cost.resource]
    return this.state.resources[resourceKey] >= skill.cost.amount
  }

  /**
   * Check if a skill's prerequisites are met
   */
  hasPrerequisites(skillId: string): boolean {
    const skill = getSkillById(skillId)
    if (!skill || !skill.requires) return true

    return skill.requires.every((req) => this.state.unlockedSkills.has(req))
  }

  /**
   * Get passive income breakdown for UI display
   */
  getIncomeBreakdown(): {
    bySkill: Map<string, number>
    byCategory: Map<string, number>
    total: InteractiveGameState['resources']
  } {
    const bySkill = new Map<string, number>()
    const byCategory = new Map<string, number>()
    const total = this.calculatePassiveIncome()

    for (const skillId of this.state.unlockedSkills) {
      const skill = getSkillById(skillId)
      if (skill?.generation) {
        bySkill.set(skillId, skill.generation.rate)

        const current = byCategory.get(skill.category) || 0
        byCategory.set(skill.category, current + skill.generation.rate)
      }
    }

    return { bySkill, byCategory, total }
  }

  /**
   * Calculate offline progress (max 4 hours)
   */
  calculateOfflineProgress(lastSaveTimestamp: number): InteractiveGameState['resources'] {
    const now = Date.now()
    const elapsed = (now - lastSaveTimestamp) / 1000 // Convert to seconds
    const maxOfflineTime = 4 * 60 * 60 // 4 hours in seconds
    const cappedElapsed = Math.min(elapsed, maxOfflineTime)

    const income = this.calculatePassiveIncome()

    return {
      infrastructure: income.infrastructure * cappedElapsed,
      automation: income.automation * cappedElapsed,
      data: income.data * cappedElapsed,
      operations: income.operations * cappedElapsed,
    }
  }

  /**
   * Apply offline progress to state
   */
  applyOfflineProgress(offlineIncome: InteractiveGameState['resources']): void {
    this.state.resources.infrastructure += offlineIncome.infrastructure
    this.state.resources.automation += offlineIncome.automation
    this.state.resources.data += offlineIncome.data
    this.state.resources.operations += offlineIncome.operations
  }
}

/**
 * Create initial game state
 */
export function createInitialState(): InteractiveGameState {
  return {
    resources: {
      infrastructure: 0, // Start from zero for progression feel
      automation: 0,
      data: 0,
      operations: 5, // Just enough to unlock first skill
    },
    resourceCaps: {
      infrastructure: 100, // Base cap, increases with skills
      automation: 100,
      data: 100,
      operations: 100,
    },
    unlockedSkills: new Set(),
    skillLevels: {},
    multipliers: {
      global: 1,
      skillUpgrade: 1,
      crossCategory: 1,
      activePlay: 1,
    },
    activeSynergies: new Set(),
    totalClicks: 0,
    playtime: 0,
    lastSave: Date.now(),
    lastTick: Date.now(),
  }
}
